/**
 * Same-event combo rule (Site Notes Function Rules): parlays may include
 * only one pick per game id. Teaser / straight / call are not restricted.
 */

import { sameEventComboMessage } from "./entryCopy";

/** Normalize the event id from a slip leg or order leg. */
export const getLegEventId = (leg) => {
  if (!leg || typeof leg !== "object") return "";
  return String(leg.id || leg.gameId || "").trim();
};

/**
 * After replacing the same market type on a game, would adding `incoming`
 * leave two legs that share an event id?
 */
export const wouldCreateSameEventCombo = (slip = [], incoming) => {
  const incomingId = getLegEventId(incoming);
  if (!incomingId) return false;

  const incomingType = incoming?.type || incoming?.oddType || "";
  const remaining = (Array.isArray(slip) ? slip : []).filter((leg) => {
    const legId = getLegEventId(leg);
    const legType = leg?.type || leg?.oddType || "";
    return !(legId === incomingId && legType === incomingType);
  });

  return remaining.some((leg) => getLegEventId(leg) === incomingId);
};

/** True when two or more legs share the same non-empty event id. */
export const hasSameEventComboConflict = (legs = []) => {
  const list = Array.isArray(legs) ? legs.filter(Boolean) : [];
  const seen = new Set();

  for (const leg of list) {
    const id = getLegEventId(leg);
    if (!id) continue;
    if (seen.has(id)) return true;
    seen.add(id);
  }

  return false;
};

/** Parlay Entry forbids two picks from one event. Teaser is unchanged. */
export const isSameEventRestrictedTab = (betTabName) =>
  betTabName === "Parlay Bet";

/**
 * When switching onto Parlay, keep the first pick per event and drop later
 * same-event legs (e.g. brought over from Teaser). Mirrors sanitizeLegsForTeaser.
 */
export const sanitizeLegsForParlaySameEvent = (legs = []) => {
  const list = Array.isArray(legs) ? legs.filter(Boolean) : [];
  if (!list.length) {
    return { legs: [], changed: false, message: null };
  }

  const seen = new Set();
  const kept = [];
  let removed = 0;

  for (const leg of list) {
    const id = getLegEventId(leg);
    if (id && seen.has(id)) {
      removed += 1;
      continue;
    }
    if (id) seen.add(id);
    kept.push(leg);
  }

  if (removed === 0) {
    return { legs: list, changed: false, message: null };
  }

  return {
    legs: kept,
    changed: true,
    message: sameEventComboMessage(),
  };
};
